import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { convertInputToUTC } from "../../util/date";
import AddTrainingsBulkMutation from "./mutation/BulkAddTrainings";
import useThemeColors from "../organization/portal/useThemeColors";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";

const EMPTY_LIST = [];

const emptyTraining = (stages) => {
    const firstStage = stages?.[0] || {};
    const firstGroup = firstStage?.groups?.[0] || {};
    return {
        stageId: firstStage?.id || "",
        groupId: firstStage?.groups?.length ? firstGroup?.id || "" : "",
        dateTime: "",
        teamIds: [],
        fieldId: "",
        refereeIds: [],
        managerIds: [],
        teams: firstStage?.groups?.length ? firstGroup?.teams || [] : firstStage?.teams || [],
        fields: firstStage?.fields || [],
        groups: firstStage?.groups || [],
    };
};

export default function BulkAddTrainingsModal({
                                                  modalId = "bulk-add-trainings-modal",
                                                  stages = [],
                                                  referees = [],
                                                  managers = [],
                                              }) {
    const { t } = useTranslation();
    const colors = useThemeColors();
    const [addTrainingsBulk] = AddTrainingsBulkMutation();
    const safeStages = Array.isArray(stages) ? stages : EMPTY_LIST;
    const safeManagers = Array.isArray(managers) ? managers : EMPTY_LIST;

    const [rows, setRows] = useState([emptyTraining(safeStages)]);
    const [saving, setSaving] = useState(false);
    const [validationErrors, setValidationErrors] = useState(null);
    const [showRightScrollCue, setShowRightScrollCue] = useState(false);
    const tableWrapRef = useRef(null);

    const scopeSel = useMemo(() => `#${modalId}`, [modalId]);

    const updateRightScrollCue = useCallback(() => {
        const el = tableWrapRef.current;
        if (!el) {
            setShowRightScrollCue(false);
            return;
        }

        const canScrollHorizontally = el.scrollWidth > el.clientWidth + 1;
        const isAtRightEdge = el.scrollLeft + el.clientWidth >= el.scrollWidth - 2;
        setShowRightScrollCue(canScrollHorizontally && !isAtRightEdge);
    }, []);

    useEffect(() => {
        const handleShow = () => setValidationErrors(null);
        window.$?.(scopeSel).on("show.bs.modal", handleShow);
        return () => window.$?.(scopeSel).off("show.bs.modal", handleShow);
    }, [scopeSel]);

    useEffect(() => {
        const handleShown = () => {
            setValidationErrors(null);
            requestAnimationFrame(() => requestAnimationFrame(updateRightScrollCue));
        };

        if (window.$ && typeof window.$(scopeSel)?.on === "function") {
            window.$(scopeSel).on("shown.bs.modal", handleShown);
            return () => window.$(scopeSel).off("shown.bs.modal", handleShown);
        }
    }, [scopeSel, updateRightScrollCue]);

    useEffect(() => {
        const el = tableWrapRef.current;
        if (!el) return;

        updateRightScrollCue();

        el.addEventListener("scroll", updateRightScrollCue, { passive: true });
        window.addEventListener("resize", updateRightScrollCue);

        return () => {
            el.removeEventListener("scroll", updateRightScrollCue);
            window.removeEventListener("resize", updateRightScrollCue);
        };
    }, [updateRightScrollCue]);

    useEffect(() => {
        updateRightScrollCue();
    }, [updateRightScrollCue, rows, safeStages]);

    const addRow = () => {
        setRows((prev) => [...prev, emptyTraining(safeStages)]);
        setValidationErrors(null);
    };

    const removeRow = (idx) => {
        setRows((prev) => prev.filter((_, i) => i !== idx));
        setValidationErrors(null);
    };

    const updateField = (idx, key, value) => {
        setRows((prev) =>
            prev.map((item, i) => {
                if (i !== idx) return item;
                let next = { ...item, [key]: value };

                if (key === "stageId") {
                    const selectedStage = safeStages.find((s) => String(s.id) === String(value));
                    next.groups = selectedStage?.groups || [];

                    if (selectedStage?.groups?.length) {
                        const firstGroup = selectedStage.groups[0];
                        next.groupId = firstGroup?.id || "";
                        next.teams = firstGroup?.teams || [];
                    } else {
                        next.groupId = "";
                        next.teams = selectedStage?.teams || [];
                    }

                    next.fields = selectedStage?.fields || [];
                    next.teamIds = [];
                    next.fieldId = "";
                    next.refereeIds = [];
                    next.managerIds = [];
                }

                if (key === "groupId") {
                    const selectedGroup = item.groups?.find((g) => String(g.id) === String(value));
                    next.teams = selectedGroup ? selectedGroup.teams : [];
                    next.teamIds = [];
                }

                return next;
            })
        );

        setValidationErrors(null);
    };

    const onMultiSelectTeams = (idx, event) => {
        const selected = Array.from(event.target.selectedOptions).map((o) => o.value);
        updateField(idx, "teamIds", selected);
    };

    const validateRows = () => {
        const errors = [];
        rows.forEach((r, i) => {
            if (!r.stageId || !r.dateTime || !r.fieldId) {
                errors.push({
                    row: i + 1,
                    error: t("bulkAddTrainingsModal.validation.completeAllFields"),
                });
            }
            if (!r.teamIds || r.teamIds.length === 0) {
                errors.push({
                    row: i + 1,
                    error: t("bulkAddTrainingsModal.validation.selectAtLeastOneTeam"),
                });
            }
        });
        return errors;
    };

    const handleBulkSubmit = async (e) => {
        e.preventDefault();
        setValidationErrors(null);

        const errs = validateRows();
        if (errs.length) {
            setValidationErrors(errs);
            return;
        }

        setSaving(true);
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

        try {
            const trainings = rows.map((r) => ({
                stageId: r.stageId,
                groupId: r.groupId || null,
                fieldId: r.fieldId,
                teamIds: r.teamIds,
                dateTime: convertInputToUTC(r.dateTime, tz),
                timezone: tz,
                refereeIds: r.refereeIds || [],
                managerIds: r.managerIds || [],
            }));

            const result = await addTrainingsBulk({ variables: { trainings } });

            if (result?.data?.addTrainingsBulk?.success) {
                toast.success(t("bulkAddTrainingsModal.messages.trainingsCreatedSuccessfully"), {
                    autoClose: 2500,
                });
                setRows([emptyTraining(safeStages)]);
                window.$?.(scopeSel).modal("hide");
                setTimeout(() => window.location.reload(), 1200);
            } else {
                let serverErrors = result?.data?.addTrainingsBulk?.errors || [];
                if (serverErrors.length && typeof serverErrors[0] === "string") {
                    serverErrors = serverErrors.map((s) => {
                        try {
                            return JSON.parse(s);
                        } catch {
                            return { row: "-", error: s };
                        }
                    });
                }
                setValidationErrors(serverErrors);
            }
        } catch (err) {
            toast.error(
                t("bulkAddTrainingsModal.messages.errorCreatingTrainings", {
                    message: getLocalizedGraphQLError(err, t, "bulkAddTrainingsModal.messages.errorCreatingTrainings"),
                })
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="modal fade kbola-bulkModal"
            id={modalId}
            tabIndex="-1"
            role="dialog"
            aria-labelledby="bulkAddTrainingsTitle"
            aria-hidden="true"
        >
            <div className="modal-dialog modal-xl" role="document">
                <div className="modal-content kbola-modalContent">
                    <form onSubmit={handleBulkSubmit}>
                        <div className="modal-header kbola-modalHeader">
                            <div className="d-flex align-items-center" style={{ gap: 10, minWidth: 0 }}>
                                <span className="kbola-pill kbola-pillBrand">
                                    {t("bulkAddTrainingsModal.bulk")}
                                </span>
                                <h4 className="modal-title mb-0 kbola-modalTitle" id="bulkAddTrainingsTitle">
                                    {t("bulkAddTrainingsModal.title")}
                                </h4>
                            </div>

                            <button
                                type="button"
                                className="close kbola-closeBtn"
                                data-dismiss="modal"
                                aria-label={t("bulkAddTrainingsModal.close")}
                            >
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>

                        <div className="modal-body kbola-modalBody">
                            {validationErrors && validationErrors.length > 0 && (
                                <div className="kbola-alertDanger">
                                    <div className="kbola-alertTitle">
                                        {t("bulkAddTrainingsModal.alerts.fixFollowing")}
                                    </div>
                                    <ul className="kbola-alertList">
                                        {validationErrors.map((err, idx) => (
                                            <li key={`${err.row}_${err.error}_${idx}`}>
                                                <strong>
                                                    {t("bulkAddTrainingsModal.alerts.rowLabel", {
                                                        row: err.row,
                                                    })}
                                                </strong>{" "}
                                                {err.error}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div
                                ref={tableWrapRef}
                                className={`table-responsive kbola-tableWrap ${
                                    showRightScrollCue ? "kbola-tableWrap--hasRightCue" : ""
                                }`}
                            >
                                <table className="table table-striped table-sm mb-0 kbola-table">
                                    <thead className="kbola-thead">
                                    <tr>
                                        <th>{t("bulkAddTrainingsModal.headers.stage")}</th>
                                        <th>{t("bulkAddTrainingsModal.headers.group")}</th>
                                        <th>{t("bulkAddTrainingsModal.headers.datetime")}</th>
                                        <th>{t("bulkAddTrainingsModal.headers.teams")}</th>
                                        <th>{t("bulkAddTrainingsModal.headers.venue")}</th>
                                        <th>{t("bulkAddTrainingsModal.headers.responsibleCoach")}</th>
                                        <th>{t("bulkAddGamesModal.headers.manager")}</th>
                                        <th className="text-right"> </th>
                                    </tr>
                                    </thead>

                                    <tbody className="kbola-tbody">
                                    {rows.map((r, idx) => {
                                        const errorForRow =
                                            validationErrors &&
                                            validationErrors.find((e) => e.row === idx + 1);

                                        return (
                                            <tr key={idx} className={errorForRow ? "kbola-rowError" : ""}>
                                                <td>
                                                    <select
                                                        className="form-control kbola-control"
                                                        value={r.stageId}
                                                        onChange={(e) => updateField(idx, "stageId", e.target.value)}
                                                        required
                                                        disabled={saving}
                                                    >
                                                        <option value="">
                                                            {t("bulkAddTrainingsModal.choose")}
                                                        </option>
                                                        {safeStages.map((s) => (
                                                            <option key={s.id} value={s.id}>
                                                                {s.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </td>

                                                {r.groups && r.groups.length > 0 ? (
                                                    <td>
                                                        <select
                                                            className="form-control kbola-control"
                                                            value={r.groupId}
                                                            onChange={(e) => updateField(idx, "groupId", e.target.value)}
                                                            required
                                                            disabled={saving}
                                                        >
                                                            <option value="">
                                                                {t("bulkAddTrainingsModal.choose")}
                                                            </option>
                                                            {r.groups.map((grp) => (
                                                                <option key={grp.id} value={grp.id}>
                                                                    {grp.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                ) : (
                                                    <td>
                                                            <span className="kbola-pill kbola-pillMuted">
                                                                {t("bulkAddTrainingsModal.notAvailable")}
                                                            </span>
                                                    </td>
                                                )}

                                                <td>
                                                    <input
                                                        type="datetime-local"
                                                        className="form-control kbola-control"
                                                        value={r.dateTime}
                                                        onChange={(e) => updateField(idx, "dateTime", e.target.value)}
                                                        required
                                                        disabled={saving}
                                                    />
                                                </td>

                                                <td>
                                                    <select
                                                        multiple
                                                        className="form-control kbola-control kbola-multi"
                                                        value={r.teamIds}
                                                        onChange={(e) => onMultiSelectTeams(idx, e)}
                                                        required
                                                        disabled={saving}
                                                        size={Math.min(6, Math.max(3, r.teams?.length || 3))}
                                                    >
                                                        {r.teams?.map((tItem) => (
                                                            <option key={tItem.teamId} value={tItem.teamId}>
                                                                {tItem.teamName}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </td>

                                                <td>
                                                    <select
                                                        className="form-control kbola-control"
                                                        value={r.fieldId}
                                                        onChange={(e) => updateField(idx, "fieldId", e.target.value)}
                                                        required
                                                        disabled={saving}
                                                    >
                                                        <option value="">
                                                            {t("bulkAddTrainingsModal.choose")}
                                                        </option>
                                                        {r.fields?.map((f) => (
                                                            <option key={f.id} value={f.id}>
                                                                {f.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </td>

                                                <td>
                                                    <select
                                                        className="form-control kbola-control"
                                                        value={r.refereeIds}
                                                        multiple
                                                        onChange={(e) => updateField(idx, "refereeIds", Array.from(e.target.selectedOptions).map(o => o.value))}
                                                        disabled={saving}
                                                    >
                                                        {(safeStages.find((stage) => String(stage.id) === String(r.stageId))?.stageReferees || []).map((rr) => (
                                                            <option key={rr.id} value={rr.id}>
                                                                {rr.fullName}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </td>

                                                <td>
                                                    <select
                                                        className="form-control kbola-control"
                                                        value={r.managerIds}
                                                        multiple
                                                        onChange={(e) => updateField(idx, "managerIds", Array.from(e.target.selectedOptions).map(o => o.value))}
                                                        disabled={saving}
                                                    >
                                                        {safeManagers.map((rr) => (
                                                            <option key={rr.id} value={rr.id}>
                                                                {rr.fullName}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </td>

                                                <td className="text-right">
                                                    {rows.length > 1 && (
                                                        <button
                                                            type="button"
                                                            className="btn kbola-iconDanger"
                                                            onClick={() => removeRow(idx)}
                                                            disabled={saving}
                                                            title={t("bulkAddTrainingsModal.removeRow")}
                                                        >
                                                            <i className="fas fa-trash" />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            </div>

                            <div className="d-flex align-items-center justify-content-between mt-3">
                                <button
                                    type="button"
                                    className="btn kbola-addRowBtn"
                                    onClick={addRow}
                                    disabled={saving}
                                >
                                    <i className="fas fa-plus mr-2" />
                                    {t("bulkAddTrainingsModal.buttons.addAnotherTraining")}
                                </button>

                                <div className="kbola-hint">
                                    {t("bulkAddTrainingsModal.hint")}
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer kbola-modalFooter">
                            <button
                                type="button"
                                className="btn btn-default kbola-secondaryBtn"
                                data-dismiss="modal"
                                disabled={saving}
                            >
                                {t("bulkAddTrainingsModal.close")}
                            </button>

                            <button
                                type="submit"
                                className="btn btn-kbola kbola-primaryBtn"
                                disabled={saving}
                            >
                                {saving ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm mr-2"
                                            role="status"
                                            aria-hidden="true"
                                        />
                                        {t("bulkAddTrainingsModal.buttons.saving")}
                                    </>
                                ) : (
                                    t("bulkAddTrainingsModal.buttons.saveAll")
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <style>{`
        ${scopeSel} { font-family: 'Poppins', sans-serif; }

        ${scopeSel} i.fas,
        ${scopeSel} i.far,
        ${scopeSel} i.fal,
        ${scopeSel} i.fa,
        ${scopeSel} i[class^="fa-"],
        ${scopeSel} i[class*=" fa-"] {
          font-family: "Font Awesome 5 Free" !important;
          font-weight: 900;
        }
        ${scopeSel} i.fab {
          font-family: "Font Awesome 5 Brands" !important;
          font-weight: 400;
        }

        ${scopeSel} .kbola-modalContent {
          border-radius: 18px;
          overflow: hidden;
          border: 1px solid rgba(0,0,0,0.08);
          box-shadow: 0 18px 44px rgba(0,0,0,0.18);
        }

        ${scopeSel} .kbola-modalHeader {
          padding: 14px 16px;
          background: ${colors.primary};
          border-bottom: 1px solid rgba(0,0,0,0.06);
        }

        ${scopeSel} .kbola-modalTitle {
          font-size: 16px;
          font-weight: 900;
          color: ${colors.textOnPrimary};
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        ${scopeSel} .kbola-closeBtn { color: ${colors.textOnPrimary}; opacity: 1; }

        ${scopeSel} .kbola-modalBody { background: #fff; }

        ${scopeSel} .kbola-modalFooter {
          padding: 12px 14px;
          border-top: 1px solid rgba(0,0,0,0.06);
          background: rgba(0,0,0,0.01);
        }

        ${scopeSel} .kbola-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 10px;
          border-radius: 999px;
          font-weight: 900;
          font-size: 12px;
          border: 1px solid rgba(0,0,0,0.10);
          line-height: 1;
          white-space: nowrap;
        }
        ${scopeSel} .kbola-pillBrand {
          background: ${colors.textOnPrimary};
          color: ${colors.primary};
          border-color: rgba(0,0,0,0.10);
        }
        ${scopeSel} .kbola-pillMuted {
          background: rgba(0,0,0,0.03);
          color: rgba(20,21,26,0.72);
          border-color: rgba(0,0,0,0.08);
          font-weight: 800;
        }

        ${scopeSel} .kbola-alertDanger {
          border-radius: 14px;
          border: 1px solid rgba(220,53,69,0.18);
          background: rgba(220,53,69,0.06);
          padding: 12px 14px;
          margin-bottom: 14px;
        }
        ${scopeSel} .kbola-alertTitle {
          font-weight: 900;
          color: rgba(220,53,69,0.95);
          margin-bottom: 6px;
        }
        ${scopeSel} .kbola-alertList {
          margin: 0;
          padding-left: 18px;
          color: rgba(20,21,26,0.85);
          font-weight: 700;
          font-size: 13px;
        }

        ${scopeSel} .kbola-tableWrap {
          position: relative;
          border-radius: 16px;
          overflow-x: auto;
          overflow-y: hidden;
          border: 1px solid rgba(0,0,0,0.08);
          box-shadow: 0 10px 26px rgba(0,0,0,0.06);
          -webkit-overflow-scrolling: touch;
        }
        ${scopeSel} .kbola-tableWrap--hasRightCue::after {
          content: "";
          position: absolute;
          top: 0;
          right: 0;
          width: 96px;
          height: 100%;
          pointer-events: none;
          background: linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,1));
          z-index: 2;
        }
        ${scopeSel} .kbola-tableWrap--hasRightCue::before {
          content: ">>>";
          position: absolute;
          top: 50%;
          right: 14px;
          transform: translateY(-50%);
          pointer-events: none;
          font-size: 30px;
          font-weight: 900;
          letter-spacing: 0.08em;
          color: ${colors.woodsmoke};
          text-shadow: 0 0 6px rgba(255,255,255,0.95);
          animation: kbola-scrollCuePulse 1s ease-in-out infinite;
          z-index: 3;
        }
        @keyframes kbola-scrollCuePulse {
          0% { opacity: 0.35; }
          50% { opacity: 0.9; }
          100% { opacity: 0.35; }
        }

        ${scopeSel} .kbola-table {
          min-width: 980px;
        }

        ${scopeSel} .kbola-thead th {
          background: rgba(0,0,0,0.02);
          border-bottom: 1px solid rgba(0,0,0,0.06);
          color: rgba(20,21,26,0.65);
          font-size: 12.5px;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.02em;
          padding: 12px 14px;
          white-space: nowrap;
          vertical-align: middle;
        }

        ${scopeSel} .kbola-tbody td {
          border-top: 1px solid rgba(0,0,0,0.06);
          padding: 10px 12px;
          vertical-align: middle;
          background: #fff;
        }

        ${scopeSel} .kbola-tbody tr:hover td {
          background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.08);
        }

        ${scopeSel} .kbola-rowError td {
          background: rgba(220,53,69,0.06) !important;
        }

        ${scopeSel} .kbola-control {
          border-radius: 12px;
          border: 1px solid rgba(0,0,0,0.10);
          font-weight: 800;
          font-size: 13px;
          padding: 10px 10px;
          box-shadow: none;
          height: 42px;
        }

        ${scopeSel} select.kbola-control[multiple],
        ${scopeSel} .kbola-multi {
          height: auto;
          padding: 10px;
        }

        ${scopeSel} .kbola-control:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
          border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.75);
        }

        ${scopeSel} .kbola-helpText {
          display: block;
          margin-top: 6px;
          font-size: 12px;
          font-weight: 800;
          color: rgba(20,21,26,0.55);
        }

        ${scopeSel} .kbola-primaryBtn {
          border-radius: 12px;
          font-weight: 900;
          padding: 10px 14px;
          background: ${colors.primary};
          color: ${colors.textOnPrimary};
        }

        ${scopeSel} .kbola-secondaryBtn {
          border-radius: 12px;
          font-weight: 900;
          padding: 10px 14px;
        }

        ${scopeSel} .kbola-addRowBtn {
          border-radius: 12px;
          font-weight: 900;
          padding: 10px 14px;
          background: ${colors.primary};
          border: 1px solid rgba(0,0,0,0.10);
          color: ${colors.textOnPrimary};
        }
        ${scopeSel} .kbola-addRowBtn:hover { background: rgba(var(--kbs-primary-rgb, 0,173,255), 1); }

        ${scopeSel} .kbola-iconDanger {
          width: 42px;
          height: 42px;
          border-radius: 12px;
          border: 1px solid rgba(220,53,69,0.18);
          background: rgba(220,53,69,0.06);
          color: rgba(220,53,69,0.95);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0;
        }
        ${scopeSel} .kbola-iconDanger:hover { background: rgba(220,53,69,0.10); }

        ${scopeSel} .kbola-hint {
          font-size: 12px;
          font-weight: 800;
          color: rgba(20,21,26,0.55);
        }
        ${scopeSel} .modal-dialog {
          width: calc(100vw - 2rem);
          max-width: 1600px;
          max-height: calc(100% - 1.75rem * 2);
        }
        
        ${scopeSel} .kbola-modalBody {
          max-height: calc(100vh - 210px);
          overflow-y: auto;
        }

        @media (max-width: 576px) {
          ${scopeSel} .modal-dialog {
            width: calc(100vw - 1rem);
            margin: 0.5rem auto;
          }
          ${scopeSel} .kbola-hint { display: none; }
          ${scopeSel} .kbola-thead th { padding: 10px 10px; }
          ${scopeSel} .kbola-tbody td { padding: 10px 10px; }
        }
      `}</style>
        </div>
    );
}
